import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LabelSheetConfig, LABEL_SHEET_PRESETS } from '@/types/label';
import { Grid3X3 } from 'lucide-react';

interface SheetConfigProps {
  config: LabelSheetConfig;
  onConfigChange: (config: LabelSheetConfig) => void;
}

export const SheetConfig: React.FC<SheetConfigProps> = ({
  config,
  onConfigChange,
}) => {
  const handlePresetChange = (presetName: string) => {
    const preset = LABEL_SHEET_PRESETS.find((p) => p.name === presetName);
    if (preset) {
      onConfigChange(preset);
    }
  };

  const updateConfig = (updates: Partial<LabelSheetConfig>) => {
    onConfigChange({ ...config, ...updates });
  };

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 pb-2 border-b border-border">
        <Grid3X3 className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-medium">Sheet Configuration</h3>
      </div>

      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">Preset</Label>
        <Select value={config.name} onValueChange={handlePresetChange}>
          <SelectTrigger className="h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {LABEL_SHEET_PRESETS.map((preset) => (
              <SelectItem key={preset.name} value={preset.name}>
                {preset.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Page Width (mm)</Label>
          <Input
            type="number"
            value={config.pageWidth}
            onChange={(e) => updateConfig({ pageWidth: parseFloat(e.target.value) || 210 })}
            className="h-8"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Page Height (mm)</Label>
          <Input
            type="number"
            value={config.pageHeight}
            onChange={(e) => updateConfig({ pageHeight: parseFloat(e.target.value) || 297 })}
            className="h-8"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Columns</Label>
          <Input
            type="number"
            min="1"
            max="10"
            value={config.columns}
            onChange={(e) => updateConfig({ columns: parseInt(e.target.value) || 1 })}
            className="h-8"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Rows</Label>
          <Input
            type="number"
            min="1"
            max="20"
            value={config.rows}
            onChange={(e) => updateConfig({ rows: parseInt(e.target.value) || 1 })}
            className="h-8"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Margin Top (mm)</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={config.marginTop}
            onChange={(e) => updateConfig({ marginTop: parseFloat(e.target.value) || 0 })}
            className="h-8"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Margin Left (mm)</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={config.marginLeft}
            onChange={(e) => updateConfig({ marginLeft: parseFloat(e.target.value) || 0 })}
            className="h-8"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">H-Gap (mm)</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={config.horizontalGap}
            onChange={(e) => updateConfig({ horizontalGap: parseFloat(e.target.value) || 0 })}
            className="h-8"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">V-Gap (mm)</Label>
          <Input
            type="number"
            min="0"
            step="0.5"
            value={config.verticalGap}
            onChange={(e) => updateConfig({ verticalGap: parseFloat(e.target.value) || 0 })}
            className="h-8"
          />
        </div>
      </div>

      <div className="pt-2 text-xs text-muted-foreground">
        <p>Labels per page: {config.columns * config.rows}</p>
        <p>Label size: {config.labelWidth} × {config.labelHeight} mm</p>
      </div>
    </div>
  );
};
